import { useTheme } from '@mui/material/styles';
import { URLS, FEATURES } from '../../config/app-config';
import React, { useState, useCallback, useEffect } from 'react';
import {
    IconButton,
    Tooltip,
    Snackbar,
    Box,
    Popover,
    List,
    ListItemButton,
    ListItemText,
    Typography,
    Divider,
    TextField,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ShareIcon from '@mui/icons-material/Share';
import PrintIcon from '@mui/icons-material/Print';
import FeedbackIcon from '@mui/icons-material/Feedback';
import CodeIcon from '@mui/icons-material/Code';

import { useAuth } from '../../auth/AuthProvider';

import {
    fetchLists,
    addToList,
    createListAndAdd,
} from '../../utils/collectionManager';
import { generatePdf } from '../../utils/jobExecution';

import { secureFetch } from "../../utils/secureFetch";

function ArticleOperations({ articleId, selectedRelease, title }) {
    const theme = useTheme();
    const { user, isAuthenticated, login } = useAuth();
    const apiBase = URLS.apiBase.replace(/\/$/, '') + '/';

    // Collection-States
    const [lists, setLists] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [loadingLists, setLoadingLists] = useState(false);
    const [newListName, setNewListName] = useState('');
    const [creatingList, setCreatingList] = useState(false);

    // Feedback-States
    const [feedbackAnchorEl, setFeedbackAnchorEl] = useState(null);
    const [feedbackReason, setFeedbackReason] = useState('');
    const [feedbackText, setFeedbackText] = useState('');
    const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
    const [feedbackEmail, setFeedbackEmail] = useState('');

    // Snackbar & PDF state
    const [snack, setSnack] = useState({ open: false, message: '' });
    const [pdfCreating, setPdfCreating] = useState(false);

    // Sobald der User eingeloggt ist, dessen E-Mail übernehmen
    useEffect(() => {
        if (isAuthenticated && user?.profile?.email) {
            setFeedbackEmail(user.profile.email);
        }
    }, [isAuthenticated, user]);

    const buildLink = useCallback(
        () =>
            `${URLS.frontendBase}${window.location.pathname}` +
            `?${selectedRelease.toSearchParams({ articleId })}`,
        [articleId, selectedRelease]
    );

    // — Share —
    const handleShare = async () => {
        const url = buildLink();
        if (navigator.share) {
            try {
                await navigator.share({ title, url });
                setSnack({ open: true, message: 'Shared successfully' });
            } catch {
                try {
                    await navigator.clipboard.writeText(url);
                    setSnack({ open: true, message: 'Link copied to clipboard' });
                } catch {
                    setSnack({ open: true, message: 'Failed to share or copy link' });
                }
            }
        } else {
            try {
                await navigator.clipboard.writeText(url);
                setSnack({ open: true, message: 'Link copied to clipboard' });
            } catch {
                alert('Could not copy the link. Please copy it manually.');
            }
        }
    };

    // — Embed / Single-Link —
    const handleEmbed = async () => {
        const url = `${buildLink()}&single=true`;
        try {
            await navigator.clipboard.writeText(url);
            setSnack({ open: true, message: 'Single Page Link was copied' });
        } catch {
            setSnack({ open: true, message: 'Single Page Link could not be copied' });
        }
    };

    // — PDF —
    const handleCreatePdf = async () => {
        if (pdfCreating) return;
        setPdfCreating(true);
        try {
            const pdfUrl = await generatePdf({
                articleIds: [articleId],
                selectedRelease,
            });
            window.open(pdfUrl, '_blank');
            setSnack({ open: true, message: 'PDF opened in new tab' });
        } catch {
            setSnack({ open: true, message: 'Failed to generate PDF' });
        } finally {
            setPdfCreating(false);
        }
    };

    // — Collections —
    const handleOpenCollections = async (e) => {
        if (!isAuthenticated) { login(); return; }
        setAnchorEl(e.currentTarget);
        setLoadingLists(true);
        try {
            const data = await fetchLists(user.access_token);
            setLists(data);
        } catch {
            console.error('Error loading lists');
        } finally {
            setLoadingLists(false);
        }
    };
    const handleCloseCollections = () => {
        setAnchorEl(null);
        setNewListName('');
    };
    const handleAdd = async (listName) => {
        try {
            await addToList(
                listName,
                { articleId, ...selectedRelease },
                user.access_token
            );
            setSnack({ open: true, message: `Added to “${listName}”` });
            handleCloseCollections();
        } catch (err) {
            setSnack({ open: true, message: err.status === 409 ? `Already in “${listName}”` : 'Error adding to collection' });
        }
    };
    const handleCreateList = async () => {
        const name = newListName.trim();
        if (!name) return;
        setCreatingList(true);
        try {
            await createListAndAdd(
                name,
                { articleId, ...selectedRelease },
                user.access_token
            );
            setSnack({ open: true, message: `Created “${name}” & added article` });
            setLists(await fetchLists(user.access_token));
            handleCloseCollections();
        } catch {
            setSnack({ open: true, message: 'Error creating collection' });
        } finally {
            setCreatingList(false);
        }
    };

    // — Feedback —
    const handleFeedbackOpen = (e) => {
        setFeedbackAnchorEl(e.currentTarget);
    };
    const handleFeedbackClose = () => {
        if (feedbackSubmitting) return;
        setFeedbackAnchorEl(null);
        setFeedbackReason('');
        setFeedbackText('');
        if (!isAuthenticated) setFeedbackEmail('');
    };

    const handleFeedbackSubmit = async () => {
        // Bei anonym: E-Mail erforderlich
        if (
            feedbackSubmitting ||
            !feedbackText.trim() ||
            !feedbackReason ||
            (!isAuthenticated && !feedbackEmail.trim())
        ) {
            return;
        }
        setFeedbackSubmitting(true);
        const payload = {
            subject: title || articleId,
            reason: feedbackReason,
            text: feedbackText.trim(),
            user: isAuthenticated ? user.profile.email : feedbackEmail.trim(),
            articleId: articleId,
            articleLink: buildLink(),
            ...selectedRelease,
        };
        try {
            console.log(JSON.stringify(payload));
            const res = await secureFetch(`${apiBase}user/feedback`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!res.ok) throw new Error(res.status);
            setSnack({ open: true, message: 'Thanks for your feedback!' });
            handleFeedbackClose();
        } catch {
            setSnack({ open: true, message: 'Failed to send feedback' });
        } finally {
            setFeedbackSubmitting(false);
        }
    };

    const collectionOpen = Boolean(anchorEl);
    const feedbackOpen = Boolean(feedbackAnchorEl);

    return (
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Share */}
            <Tooltip title="Share Article">
                <IconButton size="small" onClick={handleShare}>
                    <ShareIcon fontSize="inherit" />
                </IconButton>
            </Tooltip>

            <Tooltip title="Copy Single Article Link">
                <IconButton size="small" onClick={handleEmbed}>
                    <CodeIcon fontSize="inherit" />
                </IconButton>
            </Tooltip>

            {/* Collections */}
            {FEATURES.collections && (
                <Tooltip title="Add to Collection">
                    <IconButton size="small" onClick={handleOpenCollections}>
                        <AddCircleOutlineIcon fontSize="inherit" />
                    </IconButton>
                </Tooltip>
            )}

            {/* PDF */}
            <Tooltip title="Generate PDF">
                <span>
                    <IconButton size="small" onClick={handleCreatePdf} disabled={pdfCreating}>
                        {pdfCreating ? <CircularProgress size={20} /> : <PrintIcon fontSize="inherit" />}
                    </IconButton>
                </span>
            </Tooltip>

            {/* Feedback */}
            <Tooltip title="Feedback">
                <IconButton size="small" onClick={handleFeedbackOpen}>
                    <FeedbackIcon fontSize="inherit" />
                </IconButton>
            </Tooltip>

            {/* Collection Popover */}
            <Popover
                open={collectionOpen}
                anchorEl={anchorEl}
                onClose={handleCloseCollections}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Box sx={{ p: 2, width: 300 }}>
                    <Typography variant="subtitle1">Add to Collection</Typography>
                    <Divider sx={{ my: 1 }} />
                    {loadingLists ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <List dense>
                            {lists.map(({ name }) => (
                                <ListItemButton key={name} onClick={() => handleAdd(name)}>
                                    <ListItemText primary={name} />
                                </ListItemButton>
                            ))}
                            {lists.length === 0 && (
                                <Typography variant="body2" sx={{ p: 1 }}>
                                    No collections yet
                                </Typography>
                            )}
                        </List>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                            size="small"
                            label="New collection"
                            value={newListName}
                            onChange={e => setNewListName(e.target.value)}
                            onKeyPress={e => e.key === 'Enter' && handleCreateList()}
                        />
                        <Button
                            size="small"
                            variant="contained"
                            disabled={creatingList}
                            onClick={handleCreateList}
                            sx={{ backgroundColor: theme.palette.primary.main, color: '#fff', px: 2, py: 1 }}
                        >
                            Create
                        </Button>
                    </Box>
                </Box>
            </Popover>

            {/* Feedback Popover */}
            <Popover
                open={feedbackOpen}
                anchorEl={feedbackAnchorEl}
                onClose={handleFeedbackClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Box sx={{ p: 2, width: 400 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Send Feedback
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Please let us know how we can improve our documentation!
                    </Typography>
                    <FormControl fullWidth size="small" sx={{ mb: 1 }} disabled={feedbackSubmitting}>
                        <InputLabel id="feedback-reason-label">Reason</InputLabel>
                        <Select
                            labelId="feedback-reason-label"
                            id="feedback-reason"
                            value={feedbackReason}
                            label="Reason"
                            onChange={e => setFeedbackReason(e.target.value)}
                        >
                            <MenuItem value="Spelling Mistake">Spelling Mistake</MenuItem>
                            <MenuItem value="Wrong Information">Wrong Information</MenuItem>
                            <MenuItem value="Too Little Information">Too Little Information</MenuItem>
                            <MenuItem value="Outdated Information">Outdated Information</MenuItem>
                            <MenuItem value="Difficult to Understand">Difficult to Understand</MenuItem>
                            <MenuItem value="Other">Other...</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        size="small"
                        label="Your email"
                        placeholder="you@example.com"
                        value={feedbackEmail}
                        onChange={e => setFeedbackEmail(e.target.value)}
                        disabled={feedbackSubmitting || isAuthenticated}
                        sx={{ mb: 1 }}
                    />
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        placeholder="Write your feedback here..."
                        value={feedbackText}
                        onChange={e => setFeedbackText(e.target.value)}
                        disabled={feedbackSubmitting}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button
                            variant="contained"
                            onClick={handleFeedbackSubmit}
                            disabled={
                                feedbackSubmitting ||
                                !feedbackText.trim() ||
                                !feedbackReason ||
                                (!isAuthenticated && !feedbackEmail.trim())
                            }
                            sx={{ backgroundColor: theme.palette.primary.main, color: '#fff' }}
                        >
                            {feedbackSubmitting ? <CircularProgress size={20} /> : 'Submit'}
                        </Button>
                    </Box>
                </Box>
            </Popover>

            {/* Snackbar */}
            <Snackbar
                open={snack.open}
                autoHideDuration={2000}
                onClose={() => setSnack(s => ({ ...s, open: false }))}
                message={snack.message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Box>
    );
}

export default ArticleOperations;

